import type { PlanConfig, SubscriptionPlan } from "../types";

export type PlanOption = Exclude<SubscriptionPlan, "ENTERPRISE">;

export type PlanMetrics = {
  monthlyPrice: number;
  displayPrice: number;
  monthlyCredits: number;
  personalRooms: number;
  rate: number;
};

export const PLAN_CREDIT_OVERRIDES: Partial<Record<PlanOption, number>> = {
  PRO: 3740,
  STUDIO: 8715,
};

export const PLAN_OPTIONS: PlanOption[] = ["PERSONAL", "PRO", "STUDIO"];

export const PLAN_ORDER: Record<SubscriptionPlan, number> = {
  PERSONAL: 0,
  PRO: 1,
  STUDIO: 2,
  ENTERPRISE: 3,
};

export const PLAN_DISPLAY_NAMES: Record<SubscriptionPlan, string> = {
  PERSONAL: "Personal",
  PRO: "Pro",
  STUDIO: "Studio",
  ENTERPRISE: "Enterprise",
};

export const PLAN_FEATURES: Record<PlanOption, string[]> = {
  PERSONAL: [
    "Prepaid credits",
    "Large sessions - 250 credits / viewer hr",
    "Unlimited instant rooms",
  ],
  PRO: [
    "Standard sessions - 110 credits / viewer hr",
    "Large sessions - 225 credits / viewer hr",
    "Schedule sessions with custom URLs",
    "Additional usage support",
    "Everything in Personal",
  ],
  STUDIO: [
    "Standard sessions - 105 credits / viewer hr",
    "Large sessions - 200 credits / viewer hr",
    "Room Branding",
    "3 team seats",
    "Priority support",
  ],
};

export const PLAN_META: Record<
  PlanOption,
  {
    accentText: string;
    outline: string;
    buttonGradient: string;
    descriptor: string;
    consumptionRatePerViewerHour: number;
  }
> = {
  PERSONAL: {
    accentText: "text-slate-300",
    outline: "border-slate-500/40",
    buttonGradient: "from-slate-600 to-slate-500",
    descriptor: "Creators getting started",
    consumptionRatePerViewerHour: 125,
  },
  PRO: {
    accentText: "text-amber-400",
    outline: "border-amber-500/35",
    buttonGradient: "from-amber-500 to-orange-500",
    descriptor: "Professionals who stream regularly",
    consumptionRatePerViewerHour: 110,
  },
  STUDIO: {
    accentText: "text-purple-400",
    outline: "border-purple-500/35",
    buttonGradient: "from-purple-500 to-indigo-500",
    descriptor: "Small teams",
    consumptionRatePerViewerHour: 105,
  },
};

export function formatUsdCompact(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatRooms(value: number): string {
  if (value === -1) return "Unlimited";
  if (value < 0) return "No";
  return value.toString();
}

export function getPlanConfig(plan: SubscriptionPlan, planConfigs: PlanConfig[]) {
  return planConfigs.find((config) => config.plan === plan);
}

export function isUpgradePath(target: SubscriptionPlan, current: SubscriptionPlan): boolean {
  return PLAN_ORDER[target] > PLAN_ORDER[current];
}

export function getPlanMetrics(
  plan: PlanOption,
  planConfigs: PlanConfig[],
): PlanMetrics {
  const config = getPlanConfig(plan, planConfigs);
  const monthlyPrice = config?.basePrice ?? 0;
  const monthlyCredits = PLAN_CREDIT_OVERRIDES[plan] ?? config?.baseCredits ?? 0;

  return {
    monthlyPrice,
    displayPrice: monthlyPrice,
    monthlyCredits,
    personalRooms: config?.basePersonalRooms ?? 0,
    rate: PLAN_META[plan].consumptionRatePerViewerHour,
  };
}
